import { useState, useEffect } from "react";
import axios from "axios";
import { useQueryClient } from "react-query";
import { Modal, Button, Form } from "react-bootstrap";

export function Reachback({ sample }) {
  const [sendFailed, setSendFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [customerComments, setCustomerComments] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    setSendFailed(false);
  }, [sample._id]);

  const status = sample.reachback?.status ?? "none";
  const reason = sample.reachback?.reason ?? {};
  const isTraceError = "traceErrorCheck" in reason;
  const isBulkError = "bulkErrorCheck" in reason;
  const notificationFailed = sample.reachback?.emailFailed === true;
  const spectraComments = sample.reachback?.spectraComments;

  const sendReachbackEmail = async (comments) => {
    setSubmitting(true);
    setSendFailed(false);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/utility/send-reachback-email`,
        {
          sampleId: sample.sampleId,
          notes: sample.description,
          customerComments: comments || undefined,
        },
        { withCredentials: true },
      );
      await queryClient.invalidateQueries(["sample", sample._id]);
      setCustomerComments("");
    } catch (error) {
      console.error("Error sending reachback email:", error);
      setSendFailed(true);
    } finally {
      setSubmitting(false);
      setShowModal(false);
    }
  };

  const handleModalSubmit = async () => {
    await sendReachbackEmail(customerComments);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setCustomerComments("");
  };

  return (
    <>
      <div className='d-flex flex-row justify-content-center align-items-center flex-wrap pt-2'>
        {/* Spectra Comments — shown above all other content */}
        {spectraComments && (
          <div
            className='w-100 mb-3 p-3 rounded'
            style={{
              backgroundColor: "#f0f4ff",
              border: "1px solid #c0cdee",
            }}
          >
            <div className='d-flex align-items-center mb-1 gap-2'>
              <i
                className='bi bi-chat-left-text-fill'
                style={{ color: "#2855a0", fontSize: "1rem" }}
              ></i>
              <strong style={{ color: "#2855a0", fontSize: "1rem" }}>
                Comments from the Spectra Reachback Team
              </strong>
            </div>
            {spectraComments.generalComments && (
              <p
                className='mb-0'
                style={{
                  fontSize: "0.875rem",
                  color: "#333",
                  whiteSpace: "pre-wrap",
                }}
              >
                {spectraComments.generalComments}
              </p>
            )}
            {spectraComments.bulkComments && (
              <div className='mt-2'>
                <strong style={{ fontSize: "0.85rem", color: "#2855a0" }}>
                  Bulk Scan Comments
                </strong>
                <p
                  className='mb-0'
                  style={{
                    fontSize: "0.875rem",
                    color: "#333",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {spectraComments.bulkComments}
                </p>
              </div>
            )}
            {spectraComments.traceComments && (
              <div className='mt-2'>
                <strong style={{ fontSize: "0.85rem", color: "#2855a0" }}>
                  Trace Scan Comments
                </strong>
                <p
                  className='mb-0'
                  style={{
                    fontSize: "0.8rem",
                    color: "#333",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {spectraComments.traceComments}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Reachback complete message */}
        {status === "complete" && (
          <div className='d-flex align-items-center justify-content-center mt-2 w-100 order-2'>
            <i
              className='bi bi-check-circle-fill'
              style={{
                fontSize: "large",
                color: "green",
                marginRight: "10px",
                marginBottom: "2px",
              }}
            ></i>
            <span style={{ color: "black" }}>Reachback completed</span>
          </div>
        )}

        {/* Submit for Reachback button */}
        {status === "none" && (
          <div className='d-flex flex-row justify-content-center align-items-center flex-wrap pt-2'>
            <h6 className='mb-0 text-center me-2 text-wrap'>
              Would you like an expert to review your results?
            </h6>
            <div className='d-flex flex-column width-auto py-1'>
              <button
                type='submit'
                className='btn btn-primary align-self-center'
                style={{ minWidth: "150px" }}
                onClick={() => setShowModal(true)}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit for Reachback Service"}
              </button>
              {sendFailed && (
                <p
                  className='align-self-center text-center'
                  style={{ color: "#AE0000", fontStyle: "italic" }}
                >
                  Reachback submission failed. Please contact our team at{" "}
                  <a href='mailto:reachback@spectraplasmonics.com'>
                    reachback@spectraplasmonics.com
                  </a>{" "}
                  for further assistance.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Reachback in progress message */}
        {status === "inProgress" && (
          <div className='d-flex flex-column align-items-center justify-content-center mt-2 w-100'>
            {"modelFunctionError" in reason ? (
              <div className='d-flex align-items-center'>
                <i
                  className='bi bi-exclamation-octagon-fill'
                  style={{
                    fontSize: "small",
                    color: "#AE0000",
                    marginRight: "10px",
                  }}
                ></i>
                <span style={{ color: "#AE0000", fontWeight: "bold" }}>
                  <small>
                    There was a system error while analyzing your scans. Your
                    scans have been saved, and the Reachback team will provide
                    further details.
                  </small>
                </span>
              </div>
            ) : isBulkError || isTraceError ? (
              <div className='d-flex align-items-center'>
                <i
                  className='bi-hourglass-split'
                  style={{
                    fontSize: "small",
                    color: "#AE0000",
                    marginRight: "10px",
                  }}
                ></i>
                <span style={{ color: "#AE0000", fontWeight: "bold" }}>
                  <small>
                    {`${
                      isBulkError && isTraceError
                        ? "Bulk scan and trace scan"
                        : isBulkError
                          ? "Bulk scan"
                          : "Trace scan"
                    } results are currently undergoing expert review and should be interpreted cautiously.`}
                  </small>
                </span>
              </div>
            ) : "modelDiscrepancy" in reason ? (
              <div className='d-flex align-items-center order-last'>
                <i
                  className='bi bi-search'
                  style={{ fontSize: "14px", marginRight: "6px" }}
                ></i>
                <span>
                  <small>
                    The initial analysis suggests that additional substances
                    might be present in the sample. This sample will be reviewed
                    by one of our experts.
                  </small>
                </span>
              </div>
            ) : null}

            {notificationFailed ? (
              <div className='d-flex flex-column align-items-center gap-1'>
                <div className='d-flex align-items-center'>
                  <i
                    className='bi bi-exclamation-circle-fill'
                    style={{
                      fontSize: "small",
                      color: "#AE0000",
                      marginRight: "10px",
                    }}
                  ></i>
                  <span style={{ color: "#AE0000", fontWeight: "bold" }}>
                    <small>
                      The reachback team was not notified. Please retry below.
                    </small>
                  </span>
                </div>
                <button
                  type='button'
                  className='btn btn-sm btn-warning'
                  onClick={() => setShowModal(true)}
                  disabled={submitting}
                >
                  {submitting
                    ? "Submitting..."
                    : "Submit for Reachback Service"}
                </button>
                {sendFailed && (
                  <p
                    className='text-center'
                    style={{
                      color: "#AE0000",
                      fontStyle: "italic",
                      fontSize: "small",
                    }}
                  >
                    Retry failed. Please contact{" "}
                    <a href='mailto:reachback@spectraplasmonics.com'>
                      reachback@spectraplasmonics.com
                    </a>{" "}
                    directly.
                  </p>
                )}
              </div>
            ) : (
              <div className='d-flex align-items-center'>
                <i
                  className='bi bi-clock-fill'
                  style={{ fontSize: "small", marginRight: "10px" }}
                ></i>
                <span style={{ fontWeight: "bold" }}>
                  <small>
                    Reachback Submitted - We'll be in touch shortly!
                  </small>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Customer Comments Submission Modal */}
      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Submit for Reachback Service</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-muted' style={{ fontSize: "0.9rem" }}>
            Add any notes to help our experts review your sample. This is
            optional — include anything you think might be relevant.
          </p>
          <Form.Group>
            <Form.Label>
              <strong>Your Notes</strong>{" "}
              <span className='text-muted'>(optional)</span>
            </Form.Label>
            <Form.Control
              as='textarea'
              rows={5}
              value={customerComments}
              onChange={(e) => setCustomerComments(e.target.value)}
              maxLength={1000}
            />
            <Form.Text className='text-muted'>
              {customerComments.length}/1000 characters
            </Form.Text>
          </Form.Group>
          <p
            className='mt-3 mb-0'
            style={{ fontSize: "0.9rem", fontWeight: "bold" }}
          >
            Examples of helpful notes to include:
          </p>
          <ul
            className='mb-0 ps-3'
            style={{ fontSize: "0.82rem", color: "#555" }}
          >
            <li>Why the result was unexpected</li>
            <li>Where and how the sample was collected</li>
            <li>
              Any relevant environmental conditions (humidity, temperature)
            </li>
            <li>
              Additional comments about the physical appearance of the sample
              (color, texture, form)
            </li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleModalClose}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleModalSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit for Reachback Service"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
